
import { trackEvent } from '@@/bits/analytics'
import type { ResizeObserver } from '@@/bits/global'
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { observeSizeChange } from '@@/bits/resize_observer'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceActivityPanelStore } from '@@/pinia/surface_activity_panel'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceSharePanelStore } from '@@/pinia/surface_share_panel'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { isAppUsing } from 'bits/flip'
import { isFullscreenAvailable } from 'bits/fullscreen'
import { currentUrl, loginWithReferrerUrl, navigateTo, signUpWithReferrerUrl } from 'bits/location'
import { isRegistered } from 'bits/user_model'
import { Action, Component, Getter, Prop, State, Vue, namespace } from 'bits/vue'
import { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import type { OzMenuRowsRow } from 'library/v4/components/OzMenuRows.vue'
import OzMenuRows, {
  OzMenuRowsFocusStyle,
  OzMenuRowsRowStyle,
  OzMenuRowsSizePreset,
} from 'library/v4/components/OzMenuRows.vue'
import OzScreenSizeAwarePopoverModal from 'library/v4/components/OzScreenSizeAwarePopoverModal.vue'
import { useSurfaceContainerSizeStore } from 'pinia/surface_container_size'
import type { Id, PopoverAnchor, User } from 'types'

enum MenuItemType {
  SignUp = 'SignUp',
  LogIn = 'LogIn',
  ShowDetails = 'ShowDetails',
  ShareAndExport = 'ShareAndExport',
  Bookmark = 'Bookmark',
  Activity = 'Activity',
  Remake = 'Remake',
  Slideshow = 'Slideshow',
  ShowSettings = 'ShowSettings',
  ChangeFormat = 'ChangeFormat',
  ClearPosts = 'ClearPosts',
  Archive = 'Archive',
  Delete = 'Delete',
  ToggleFullScreen = 'ToggleFullScreen',
  ShowHelp = 'ShowHelp',
  Report = 'Report',
  Developers = 'Developers',
  Templates = 'Templates',
  Freeze = 'Freeze',
}

const settingsStore = namespace('settings')
const wallCollectionsStore = namespace('wallCollections')
const surfaceSharePanelStore = useSurfaceSharePanelStore()

@Component({
  components: {
    OzMenuRows,
    OzScreenSizeAwarePopoverModal,
    OzDivider,
  },
})
export default class SurfaceActionBarMoreMenu extends Vue {
  @Prop({ required: true }) popoverAnchor!: PopoverAnchor

  OzBoxColors = OzBoxColors
  OzMenuRowsFocusStyle = OzMenuRowsFocusStyle
  OzMenuRowsSizePreset = OzMenuRowsSizePreset
  OzDividerColorScheme = OzDividerColorScheme

  @State wall
  @State user!: User
  @Getter dir!: string
  @Getter canIAdminister!: boolean
  @Getter amIOwner!: boolean
  @Getter isTemplate!: boolean
  @Getter canIBookmarkWall!: boolean
  @Getter canIRemake!: boolean
  @Getter isSectionBreakout!: boolean
  @PiniaMapState(useDarkModeStore) isDarkMode!: ReturnType<typeof useDarkModeStore>['isDarkMode']
  @PiniaMapState(useWindowSizeStore) windowHeight!: ReturnType<typeof useWindowSizeStore>['windowHeight']
  @PiniaMapState(useWindowSizeStore) isPhone!: ReturnType<typeof useWindowSizeStore>['isPhone']
  @PiniaMapState(useSurfaceContainerSizeStore) isContainerSmallerThanTabletPortrait!: boolean

  @PiniaMapState(useSurfaceStore) isFrozen!: boolean
  resizeObserver: ResizeObserver | null = null

  // HTML element of surface container
  surfaceContainer = document.getElementById('surface-container') as HTMLElement

  mounted(): void {
    this.$nextTick(() => {
      this.resizeObserver = observeSizeChange(this.surfaceContainer, this.triggerResize)
    })
  }

  beforeDestroy(): void {
    this.resizeObserver?.unobserve(this.surfaceContainer)
  }

  triggerResize(): void {
    this.$emit('trigger-anchor-update')
  }

  get isUserRegistered(): boolean {
    return isRegistered(this.user)
  }

  get menuItemGroups(): OzMenuRowsRow[][] {
    // Group 1
    const group1: OzMenuRowsRow[] = this.isUserRegistered
      ? []
      : [
          {
            icon: 'quill',
            text: this.__('Sign up'),
            href: signUpWithReferrerUrl(currentUrl()),
            testId: 'surfaceMoreMenuSignUp',
            eventPayload: MenuItemType.SignUp,
          },
          {
            icon: 'fingerprint',
            text: this.__('Log in'),
            href: loginWithReferrerUrl(currentUrl()),
            testId: 'surfaceMoreMenuLogIn',
            eventPayload: MenuItemType.LogIn,
          },
        ]

    // Group 2
    const group2: OzMenuRowsRow[] = [
      {
        icon: 'info_outline',
        text: this.__('Details'),
        testId: 'surfaceMoreMenuShowDetails',
        eventPayload: MenuItemType.ShowDetails,
      },
      {
        icon: this.isActiveWallBookmarked ? 'star_edit' : 'star_outline',
        text: this.isActiveWallBookmarked ? this.__('Edit bookmark') : this.__('Add bookmark'),
        isDisabled: !this.canIBookmarkWall || this.isSectionBreakout,
        rowStyle:
          this.canIBookmarkWall && !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuBookmark',
        eventPayload: MenuItemType.Bookmark,
      },
      {
        icon: 'share_arrow_outline',
        text: this.__('Share'),
        isDisabled: this.isSectionBreakout,
        rowStyle: !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuShareAndExport',
        eventPayload: MenuItemType.ShareAndExport,
      },
      {
        icon: 'arrow_2_rectangular_clockwise_thin',
        text: this.__('Remake'),
        isDisabled: !this.canIRemake || this.isSectionBreakout,
        rowStyle: this.canIRemake && !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuRemake',
        eventPayload: MenuItemType.Remake,
      },
      {
        icon: 'bell',
        text: this.__('Activity'),
        isDisabled: this.isSectionBreakout,
        rowStyle: !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuActivity',
        eventPayload: MenuItemType.Activity,
      },
      {
        icon: 'play_outline',
        text: this.__('Slideshow'),
        isDisabled: this.isSectionBreakout,
        rowStyle: !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuSlideshow',
        eventPayload: MenuItemType.Slideshow,
      },
      {
        icon: this.isTemplate ? 'sub_grid_slash' : 'sub_grid',
        text: this.isTemplate ? this.__('Unmark as template') : this.__('Mark as template'),
        isDisabled: this.isSectionBreakout || !this.canIAdminister,
        rowStyle:
          !this.isSectionBreakout && this.canIAdminister ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuToggleAsTemplate',
        eventPayload: MenuItemType.Templates,
      },
    ]

    // Group 3
    const group3: OzMenuRowsRow[] = [
      {
        icon: 'gear_outline',
        text: this.__('Settings'),
        isDisabled: !this.canIAdminister || this.isSectionBreakout || this.isFrozen,
        rowStyle:
          this.canIAdminister && !this.isSectionBreakout && !this.isFrozen
            ? OzMenuRowsRowStyle.Default
            : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuShowSettings',
        eventPayload: MenuItemType.ShowSettings,
      },
      {
        icon: 'gallery_outline',
        text: this.__('Change format'),
        isDisabled: !this.canIAdminister || this.isSectionBreakout,
        rowStyle:
          this.canIAdminister && !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuChangeFormat',
        eventPayload: MenuItemType.ChangeFormat,
      },
      {
        icon: 'code_thin',
        text: this.__('Developer'),
        isDisabled: !this.canIAdminister || this.isSectionBreakout,
        rowStyle:
          this.canIAdminister && !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuDevelopers',
        eventPayload: MenuItemType.Developers,
      },
      {
        icon: 'erase',
        text: this.__('Clear posts'),
        isDisabled: !this.canIAdminister || this.isSectionBreakout || this.isFrozen,
        rowStyle:
          this.canIAdminister && !this.isSectionBreakout && !this.isFrozen
            ? OzMenuRowsRowStyle.Default
            : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuClearPosts',
        eventPayload: MenuItemType.ClearPosts,
      },
      {
        icon: 'archive_outline',
        text: this.__('Archive padlet'),
        isDisabled: !this.canIAdminister || this.isSectionBreakout,
        rowStyle:
          this.canIAdminister && !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuArchiveWall',
        eventPayload: MenuItemType.Archive,
      },
    ]

    if (isAppUsing('layoutFormatSettingV2')) {
      // remove "Change format" from group 3
      group3.splice(1, 1)
    }

    if (isAppUsing('reportButton')) {
      group3.push({
        icon: 'flag_outline',
        text: this.__('Report padlet'),
        isDisabled: this.isSectionBreakout,
        rowStyle: !this.isSectionBreakout ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        testId: 'surfaceMoreMenuReportWall',
        eventPayload: MenuItemType.Report,
      })
    }

    // "Freeze padlet" menu item should be between "clear posts" and "archive" menu items
    if (isAppUsing('freezePadlet')) {
      const archiveItemIndex = group3.findIndex((item) => item.eventPayload === MenuItemType.Archive)
      group3.splice(archiveItemIndex, 0, {
        icon: this.isFrozen ? 'unfreeze' : 'freeze',
        text: this.isFrozen ? this.__('Unfreeze padlet') : this.__('Freeze padlet'),
        isDisabled: !this.canIAdminister,
        rowStyle: this.canIAdminister ? OzMenuRowsRowStyle.Default : OzMenuRowsRowStyle.Disabled,
        eventPayload: MenuItemType.Freeze,
        testId: 'surfaceMoreMenuFreezePadlet',
      })
    }

    group3.push({
      icon: 'delete',
      text: this.__('Move to trash'),
      isDisabled: !this.canIAdminister || this.isSectionBreakout,
      rowStyle:
        this.canIAdminister && !this.isSectionBreakout ? OzMenuRowsRowStyle.Danger : OzMenuRowsRowStyle.Disabled,
      testId: 'surfaceMoreMenuDeleteWall',
      eventPayload: MenuItemType.Delete,
    })

    // Group 4
    const group4: OzMenuRowsRow[] = []
    if (isFullscreenAvailable) {
      group4.push({
        icon: 'fullscreen',
        text: this.isFullscreen ? this.__('Exit full screen') : this.__('Go full screen'),
        testId: 'surfaceMoreMenuToggleFullScreen',
        eventPayload: MenuItemType.ToggleFullScreen,
      })
    }
    group4.push({
      icon: 'help',
      text: this.__('Help'),
      href: 'https://padlet.help/',
      testId: 'surfaceMoreMenuShowHelp',
      eventPayload: MenuItemType.ShowHelp,
    })

    return [group1, group2, group3, group4].filter((group) => group.length > 0)
  }

  @Action showInfo!: () => void
  @Action bookmarkWallAndNotify!: () => void
  @Action openEditBookmarksDialog!: () => void
  @Action showRemakePanel!: () => void
  @Action showConvertLayoutPanel!: () => void
  @Action showClearAllDialog!: () => void
  @Action archiveWall!: () => void
  @Action showDeleteDialog!: () => void
  @Action trashWall!: () => void
  @Action showReportPanel!: ({ postId }?: { postId?: Id }) => void
  @Action showDevelopersPanel!: () => void
  @Action markWallAsTemplate!: () => void
  @Action unmarkWallAsTemplate!: () => void
  @PiniaMapAction(useSurfaceActivityPanelStore) showActivityPanel!: () => void
  @PiniaMapState(useWindowSizeStore) isFullscreen!: boolean
  @PiniaMapAction(useWindowSizeStore) enterFullscreen!: () => void
  @PiniaMapAction(useWindowSizeStore) exitFullscreen!: () => void
  @settingsStore.Action toggleSettingsPanel!: () => void
  @wallCollectionsStore.Getter isActiveWallBookmarked!: boolean
  @PiniaMapState(useSlideshowStore) coverPageUrl!: string

  goToSignUpPage(): void {
    navigateTo(signUpWithReferrerUrl(currentUrl()))
  }

  goToLogInPage(): void {
    navigateTo(loginWithReferrerUrl(currentUrl()))
  }

  showPadletInfo(): void {
    trackEvent('Surface More Menu', 'Clicked details')
    this.showInfo()
  }

  showSharePanel(): void {
    trackEvent('Surface More Menu', 'Clicked share')
    surfaceSharePanelStore.xSurfaceSharePanel = true
  }

  addOrEditBookmark(): void {
    if (this.isActiveWallBookmarked) {
      trackEvent('Surface More Menu', 'Clicked edit bookmark')
      this.openEditBookmarksDialog()
    } else {
      trackEvent('Surface More Menu', 'Clicked add bookmark')
      this.bookmarkWallAndNotify()
    }
  }

  openActivityPanel(): void {
    trackEvent('Surface More Menu', 'Clicked activity')
    this.showActivityPanel()
  }

  openRemakePanel(): void {
    trackEvent('Surface More Menu', 'Clicked remake')
    this.showRemakePanel()
  }

  openSlideshow(): void {
    if (this.wall.links.show) {
      trackEvent('Surface', 'Opened slideshow')
      navigateTo(this.coverPageUrl, { target: '_blank' })
    }
  }

  openSettingsPanel(): void {
    trackEvent('Surface More Menu', 'Clicked settings')
    this.toggleSettingsPanel()
  }

  openChangeFormatPanel(): void {
    trackEvent('Surface More Menu', 'Clicked change format')
    this.showConvertLayoutPanel()
  }

  showClearAllPostsDialog(): void {
    trackEvent('Surface More Menu', 'Clicked clear posts')
    this.showClearAllDialog()
  }

  archiveCurrentWall(): void {
    trackEvent('Surface More Menu', 'Clicked archive padlet')
    this.archiveWall()
  }

  showDeleteWallDialog(): void {
    trackEvent('Recently Deleted', 'Padlet trashed', 'Surface')
    this.trashWall()
  }

  markOrUnmarkWallAsTemplate(): void {
    if (this.isTemplate) {
      this.unmarkWallAsTemplate()
      trackEvent('Surface More Menu', 'Clicked unmark as template')
      return
    }

    this.markWallAsTemplate()
    trackEvent('Surface More Menu', 'Clicked mark as template')
  }

  toggleFullScreen(): void {
    if (!isFullscreenAvailable) return
    if (this.isFullscreen) {
      trackEvent('Surface More Menu', 'Clicked exit fullscreen')
      this.exitFullscreen()
    } else {
      trackEvent('Surface More Menu', 'Clicked go fullscreen')
      this.enterFullscreen()
    }
  }

  openDevelopersPanel(): void {
    trackEvent('Surface More Menu', 'Clicked developers')
    this.showDevelopersPanel()
  }

  goToHelpPage(): void {
    trackEvent('Surface More Menu', 'Clicked help')
    navigateTo('https://padlet.help/', { target: '_blank' })
  }

  reportCurrentWall(): void {
    trackEvent('Surface More Menu', 'Clicked report padlet')
    this.showReportPanel()
  }

  openFreezePanel(): void {
    useSurfaceFreezePadletPanel().showSurfaceFreezePadletPanel()
  }

  get handlers(): Record<MenuItemType, () => void> {
    return {
      [MenuItemType.SignUp]: this.goToSignUpPage,
      [MenuItemType.LogIn]: this.goToLogInPage,
      [MenuItemType.ShowDetails]: this.showPadletInfo,
      [MenuItemType.ShareAndExport]: this.showSharePanel,
      [MenuItemType.Bookmark]: this.addOrEditBookmark,
      [MenuItemType.Activity]: this.openActivityPanel,
      [MenuItemType.Remake]: this.openRemakePanel,
      [MenuItemType.Slideshow]: this.openSlideshow,
      [MenuItemType.ShowSettings]: this.openSettingsPanel,
      [MenuItemType.ChangeFormat]: this.openChangeFormatPanel,
      [MenuItemType.ClearPosts]: this.showClearAllPostsDialog,
      [MenuItemType.Archive]: this.archiveCurrentWall,
      [MenuItemType.Delete]: this.showDeleteWallDialog,
      [MenuItemType.ToggleFullScreen]: this.toggleFullScreen,
      [MenuItemType.ShowHelp]: this.goToHelpPage,
      [MenuItemType.Report]: this.reportCurrentWall,
      [MenuItemType.Developers]: this.openDevelopersPanel,
      [MenuItemType.Templates]: this.markOrUnmarkWallAsTemplate,
      [MenuItemType.Freeze]: this.openFreezePanel,
    }
  }

  handleSelectedMenuItem(_: unknown, item: MenuItemType): void {
    this.$emit('close')
    this.handlers[item]()
  }

  onSwipedown({ isAtTopBeforeDragging }: { isAtTopBeforeDragging: boolean }): void {
    if (!isAtTopBeforeDragging) {
      this.$emit('close')
    }
  }
}
